import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, ToggleInput } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchCancelationReasonActions as FetchActions,
  UpdateCancelationReasonActions as UpdateActions,
} from 'store/ducks/cancelationReasons';
import { UpdateCancelationReasonValidator } from 'validators/CancelationReasons';
import * as S from './styles';

interface Props {
  reasonId: string | number;
  onUpdate?: () => void;
}

export const ReasonUpdateForm: React.FC<Props> = ({ reasonId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: cancelationReason } = useSelector(
    (state: RootState) => state.fetchCancelationReason
  );

  const { loading: updatingCancelationReason, validationErrors } = useSelector(
    (state: RootState) => state.updateCancelationReason
  );

  const fetchCancelationReason = useCallback(() => {
    dispatch(FetchActions.request(reasonId));
  }, [dispatch, reasonId]);

  const onCancelationReasonLoad = useCallback((): void => {
    if (!cancelationReason) return;
    const { name, blockedAt } = cancelationReason;

    formRef.current?.setData({
      name,
      blockedAt: blockedAt ? '1' : '0',
    });
  }, [cancelationReason]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateCancelationReasonValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(reasonId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, reasonId]
  );

  useEffect(() => {
    fetchCancelationReason();
  }, [fetchCancelationReason]);

  useEffect(() => {
    onCancelationReasonLoad();
  }, [onCancelationReasonLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Input name="name" label="Nome" />
          <ToggleInput name="blockedAt" label="Bloqueada" />
        </S.FormRow>
        <S.FormActions>
          <S.LinkButton
            mood="light"
            to="/configuracoes/justificativas-cancelamento"
          >
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {updatingCancelationReason ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
